import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import {
  refreshCalendar,
  useAppointment,
  useCatalog,
  type AppointmentDetail,
  type CatalogCategory,
  type CatalogService,
  type CustomerSummary,
} from '../../lib/calendar';
import { useLocale, useT, type TKey } from '../../lib/i18n';
import { addDays, dateOf, formatDateLong, formatDateShort, formatStamp, formatTime, hhmm, instantAt, minuteOfDay, todayIn } from '../../lib/time';
import { formatEuro, Select, Switch, TextArea } from '../forms';
import { Button, ErrorNote, Field } from '../ui';

export type StaffOption = { id: string; displayName: string; color: string };
/** What the form would book, drawn in the calendar while the panel is open. */
export type Draft = { date: string; minute: number; minutes: number; staffId: string | null; label: string };
export type Prefill = {
  date: string;
  minute?: number;
  /** Length of a range dragged on the calendar. */
  minutes?: number;
  staffId: string | null;
  items?: Array<{ serviceId: string; variantId: string | null }>;
  customer?: CustomerSummary | null;
};
type Conflict = { message: string; canForce: boolean; alternatives: Array<{ start: string; staffMemberId: string }> };

const conflictOf = (e: unknown): Conflict | null =>
  e instanceof ApiError && e.code === 'slot_unavailable'
    ? { message: e.message, canForce: !!e.details?.canForce, alternatives: (e.details?.alternatives as Conflict['alternatives']) ?? [] }
    : null;
const messageOf = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);

function findService(categories: CatalogCategory[] | undefined, id: string) {
  for (const c of categories ?? []) for (const s of c.services) if (s.id === id) return s;
  return undefined;
}
/** Dutch numbers the way people read them: +31612345678 → 06 12345678. */
export function formatPhone(e164: string) {
  if (e164.startsWith('+316')) return `06 ${e164.slice(4)}`;
  if (e164.startsWith('+31')) return `0${e164.slice(3)}`;
  return e164;
}
const canDo = (s: CatalogService | undefined, staffId: string) => !s || s.staff === 'all' || s.staff.includes(staffId);

// ---------- Client picker ----------

function ClientPicker({
  orgId,
  value,
  onChange,
  newClient,
  onNewClient,
}: {
  orgId: string;
  value: CustomerSummary | null;
  onChange: (c: CustomerSummary | null) => void;
  newClient: { firstName: string; lastName: string; phone: string; email: string } | null;
  onNewClient: (v: { firstName: string; lastName: string; phone: string; email: string } | null) => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => window.clearTimeout(id);
  }, [q]);
  const results = useQuery({
    queryKey: ['customers', orgId, debounced],
    queryFn: () => api<{ customers: CustomerSummary[] }>(`/orgs/${orgId}/customers?${new URLSearchParams({ q: debounced })}`).then((r) => r.customers),
    enabled: debounced.length >= 2,
  });

  if (value) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-[var(--radius-control)] border border-line bg-surface px-3 py-2">
        <div>
          <p className="font-semibold">{[value.firstName, value.lastName].filter(Boolean).join(' ')}</p>
          {value.phone && <p className="text-sm text-muted">{formatPhone(value.phone)}</p>}
          {value.alertNote && <p className="mt-1 text-sm text-warning">⚠ {value.alertNote}</p>}
        </div>
        <button type="button" className="text-sm font-semibold text-accent-strong hover:underline" onClick={() => onChange(null)}>
          {t('appt.change')}
        </button>
      </div>
    );
  }

  if (newClient) {
    return (
      <div className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-line bg-surface p-3">
        <div className="grid grid-cols-2 gap-3">
          <Field id="nc-first" label={t('appt.firstName')} value={newClient.firstName} autoComplete="off" onChange={(e) => onNewClient({ ...newClient, firstName: e.target.value })} />
          <Field id="nc-last" label={t('appt.lastName')} value={newClient.lastName} autoComplete="off" onChange={(e) => onNewClient({ ...newClient, lastName: e.target.value })} />
        </div>
        <Field id="nc-phone" type="tel" inputMode="tel" label={t('appt.phone')} value={newClient.phone} autoComplete="off" onChange={(e) => onNewClient({ ...newClient, phone: e.target.value })} />
        <Field id="nc-email" type="email" label={t('appt.emailOptional')} value={newClient.email} autoComplete="off" onChange={(e) => onNewClient({ ...newClient, email: e.target.value })} />
        <button type="button" className="self-start text-sm text-muted hover:underline" onClick={() => onNewClient(null)}>
          {t('common.cancel')}
        </button>
      </div>
    );
  }

  const looksLikePhone = /^[\d\s+()-]+$/.test(q.trim());
  return (
    <div className="flex flex-col gap-2">
      <input
        aria-label={t('appt.searchClient')}
        placeholder={t('appt.searchClient')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-base outline-none focus:border-accent"
      />
      {debounced.length >= 2 && results.data && (
        <ul className="flex flex-col overflow-hidden rounded-[var(--radius-control)] border border-line bg-surface">
          {results.data.length === 0 && <li className="px-3 py-2 text-sm text-muted">{t('appt.noResults')}</li>}
          {results.data.map((c) => (
            <li key={c.id}>
              <button type="button" className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-subtle" onClick={() => onChange(c)}>
                <span className="font-medium">
                  {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                  {c.alertNote && <span className="ml-1 text-warning">⚠</span>}
                </span>
                <span className="text-sm text-muted">{c.phone ? formatPhone(c.phone) : (c.email ?? '')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="self-start text-sm font-semibold text-accent-strong hover:underline"
        onClick={() => {
          const [first = '', ...rest] = looksLikePhone ? [] : q.trim().split(/\s+/);
          onNewClient({ firstName: first, lastName: rest.join(' '), phone: looksLikePhone ? q.trim() : '', email: '' });
        }}
      >
        + {t('appt.newClient')}
      </button>
    </div>
  );
}

// ---------- Create / edit ----------

export function AppointmentForm({
  orgId,
  locationId,
  tz,
  staff,
  role,
  prefill,
  editing,
  onDone,
  onDraft,
}: {
  orgId: string;
  locationId: string;
  tz: string;
  staff: StaffOption[];
  role: 'owner' | 'manager' | 'staff';
  prefill: Prefill;
  editing?: { id: string; version: number; customer: CustomerSummary | null; note: string | null };
  onDone: (appointment: AppointmentDetail) => void;
  onDraft?: (draft: Draft | null) => void;
}) {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const catalog = useCatalog(orgId);
  const [customer, setCustomer] = useState<CustomerSummary | null>(editing?.customer ?? prefill.customer ?? null);
  const [newClient, setNewClient] = useState<{ firstName: string; lastName: string; phone: string; email: string } | null>(null);
  const [items, setItems] = useState(prefill.items?.length ? prefill.items : [{ serviceId: '', variantId: null as string | null }]);
  const [staffId, setStaffId] = useState<string>(prefill.staffId ?? '');
  const [date, setDate] = useState(prefill.date);
  const [time, setTime] = useState(hhmm(prefill.minute ?? 600));
  const [walkIn, setWalkIn] = useState(false);
  const [note, setNote] = useState(editing?.note ?? '');
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [notify, setNotify] = useState(true);
  const clientEmail = customer?.email ?? (newClient?.email.trim() || null);

  const chosen = items.map((i) => ({ ...i, service: findService(catalog.data, i.serviceId) }));
  const complete = chosen.every((c) => c.service && (c.service.variants.length === 0 || c.variantId));
  const minutes = chosen.reduce((n, c) => n + (c.service?.variants.find((v) => v.id === c.variantId)?.clientMinutes ?? c.service?.clientMinutes ?? 0), 0);
  const price = chosen.reduce((n, c) => n + (c.service?.variants.find((v) => v.id === c.variantId)?.priceCents ?? c.service?.priceCents ?? 0), 0);
  const fromPrice = chosen.some((c) => c.service?.priceType === 'from');
  const [h, m] = time.split(':').map(Number) as [number, number];
  const startMinute = h * 60 + m;
  const person = staff.find((s) => s.id === staffId);

  // Draw the appointment in the calendar as it takes shape (the dragged range until services are chosen).
  const draftName = newClient?.firstName.trim() || (customer ? customer.firstName : '') || chosen.find((c) => c.service)?.service?.name || t('appt.newTitle');
  const draftMinutes = complete && minutes > 0 ? minutes : (prefill.minutes ?? 30);
  useEffect(() => {
    onDraft?.({ date, minute: startMinute, minutes: draftMinutes, staffId: staffId || null, label: draftName });
  }, [onDraft, date, startMinute, draftMinutes, staffId, draftName]);
  useEffect(() => () => onDraft?.(null), [onDraft]);

  // Default the first service when the salon has only one, to save a tap.
  useEffect(() => {
    const all = catalog.data?.flatMap((c) => c.services) ?? [];
    if (all.length === 1 && items.length === 1 && !items[0]!.serviceId) setItems([{ serviceId: all[0]!.id, variantId: null }]);
  }, [catalog.data, items]);

  const payloadItems = items.filter((i) => i.serviceId).map((i) => ({ serviceId: i.serviceId, variantId: i.variantId }));
  const free = useMutation({
    mutationFn: () =>
      api<{ times: Array<{ start: string; end: string; staffMemberId: string }> }>(`/orgs/${orgId}/free-times`, {
        method: 'POST',
        body: JSON.stringify({
          locationId,
          items: payloadItems,
          staffMemberId: staffId || null,
          from: instantAt(date < todayIn(tz) ? todayIn(tz) : date, 0, tz),
          limit: 10,
          excludeAppointmentId: editing?.id,
        }),
      }).then((r) => r.times),
  });

  const save = useMutation({
    mutationFn: async (force: boolean) => {
      const startsAt = instantAt(date, startMinute, tz);
      if (editing) {
        return api<{ appointment: AppointmentDetail }>(`/orgs/${orgId}/appointments/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify({ customerId: customer?.id ?? null, items: payloadItems, staffMemberId: staffId, startsAt, internalNote: note, version: editing.version, force, notifyClient: notify }),
        });
      }
      return api<{ appointment: AppointmentDetail }>(`/orgs/${orgId}/appointments`, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify({
          locationId,
          customerId: customer?.id ?? null,
          newCustomer: newClient && newClient.firstName.trim() ? { firstName: newClient.firstName, lastName: newClient.lastName, phone: newClient.phone, email: newClient.email } : undefined,
          items: payloadItems,
          staffMemberId: staffId || null,
          startsAt,
          internalNote: note,
          source: walkIn ? 'walk_in' : 'app',
          force,
          notifyClient: notify && !!clientEmail && !walkIn,
        }),
      });
    },
    onMutate: () => setConflict(null),
    onSuccess: (r) => {
      refreshCalendar(qc, orgId, r.appointment.id);
      onDone(r.appointment);
    },
    onError: (e) => setConflict(conflictOf(e)),
  });

  const existing = save.error instanceof ApiError && save.error.code === 'customer_exists' ? (save.error.details?.customer as CustomerSummary | undefined) : undefined;

  function useNow() {
    const now = Date.now();
    setDate(dateOf(now, tz));
    setTime(hhmm(Math.floor(minuteOfDay(now, tz) / 5) * 5));
    setWalkIn(true);
  }

  const timeOptions = useMemo(() => Array.from({ length: (24 * 60) / 5 }, (_, i) => hhmm(i * 5)), []);

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(false);
      }}
    >
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('appt.client')}</h3>
        <ClientPicker orgId={orgId} value={customer} onChange={setCustomer} newClient={newClient} onNewClient={setNewClient} />
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('appt.services')}</h3>
        {catalog.data && catalog.data.every((c) => c.services.length === 0) && (
          <p className="rounded-[var(--radius-control)] bg-warning-soft px-3 py-2 text-sm text-warning">
            {t('appt.noServices')}{' '}
            <Link to="/s/$orgId/salon/diensten" params={{ orgId }} className="font-semibold underline">
              {t('appt.addServices')}
            </Link>
          </p>
        )}
        {chosen.map((c, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line bg-surface p-2">
            <div className="flex items-center gap-2">
              <select
                aria-label={t('appt.chooseService')}
                value={c.serviceId}
                onChange={(e) => setItems(items.map((it, j) => (j === i ? { serviceId: e.target.value, variantId: null } : it)))}
                className="min-h-11 w-full rounded-[var(--radius-control)] border border-line bg-surface px-2 text-base"
              >
                <option value="">{t('appt.chooseService')}</option>
                {catalog.data?.map((cat) => (
                  <optgroup key={cat.id} label={cat.name}>
                    {cat.services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} · {s.clientMinutes} min · {s.priceType === 'from' ? 'v.a. ' : ''}
                        {formatEuro(s.priceCents)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {items.length > 1 && (
                <button type="button" aria-label={t('common.delete')} className="min-h-11 rounded-[var(--radius-control)] px-3 text-muted hover:bg-subtle" onClick={() => setItems(items.filter((_, j) => j !== i))}>
                  ✕
                </button>
              )}
            </div>
            {c.service && c.service.variants.length > 0 && (
              <select
                aria-label={t('appt.chooseVariant')}
                value={c.variantId ?? ''}
                onChange={(e) => setItems(items.map((it, j) => (j === i ? { ...it, variantId: e.target.value || null } : it)))}
                className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-2 text-base"
              >
                <option value="">{t('appt.chooseVariant')}</option>
                {c.service.variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} · {v.clientMinutes} min · {formatEuro(v.priceCents)}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
        {items.length < 6 && (
          <button type="button" className="self-start text-sm font-semibold text-accent-strong hover:underline" onClick={() => setItems([...items, { serviceId: '', variantId: null }])}>
            {t('appt.addService')}
          </button>
        )}
      </section>

      <Select id="appt-staff" label={t('appt.staff')} value={staffId} onChange={(e) => setStaffId(e.target.value)}>
        {!editing && <option value="">{t('appt.noPreference')}</option>}
        {staff.map((s) => (
          <option key={s.id} value={s.id} disabled={chosen.some((c) => !canDo(c.service, s.id))}>
            {s.displayName}
          </option>
        ))}
      </Select>

      <section className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="appt-date"
            type="date"
            label={t('appt.date')}
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setWalkIn(false);
            }}
          />
          <Select
            id="appt-time"
            label={t('appt.time')}
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setWalkIn(false);
            }}
          >
            {!timeOptions.includes(time) && <option value={time}>{time}</option>}
            {timeOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <button type="button" className="text-sm font-semibold text-accent-strong hover:underline disabled:opacity-50" disabled={!complete || free.isPending} onClick={() => free.mutate()}>
            {t('appt.findFree')}
          </button>
          {!editing && (
            <button type="button" className="text-sm font-semibold text-accent-strong hover:underline" onClick={useNow}>
              {t('appt.now')}
            </button>
          )}
        </div>
        {free.data && (
          <div className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-subtle p-3">
            <p className="text-sm font-semibold">{t('appt.freeTimes')}</p>
            {free.data.length === 0 && <p className="text-sm text-muted">{t('appt.noFreeTimes')}</p>}
            <div className="flex flex-wrap gap-2">
              {free.data.map((f) => (
                <button
                  key={f.start + f.staffMemberId}
                  type="button"
                  className="rounded-full border border-line bg-surface px-3 py-1.5 text-sm hover:border-accent"
                  onClick={() => {
                    setDate(dateOf(f.start, tz));
                    setTime(hhmm(minuteOfDay(f.start, tz)));
                    setWalkIn(false);
                    if (staffId) setStaffId(f.staffMemberId);
                    free.reset();
                  }}
                >
                  {formatDateShort(dateOf(f.start, tz), locale)} {formatTime(f.start, tz)}
                  {!staffId && <span className="text-muted"> · {staff.find((s) => s.id === f.staffMemberId)?.displayName}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        <ErrorNote message={messageOf(free.error, t('error.generic'))} />
      </section>

      <TextArea id="appt-note" label={t('appt.note')} placeholder={t('appt.notePlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} />

      {clientEmail && !walkIn && (
        <Switch
          label={editing ? t('appt.notifyChange') : t('appt.notify')}
          hint={editing ? t('appt.notifyChangeHint') : t('appt.notifyHint', { email: clientEmail })}
          checked={notify}
          onChange={setNotify}
        />
      )}

      {complete && minutes > 0 && (
        <p className="rounded-[var(--radius-control)] bg-accent-soft px-3 py-2 text-[15px] font-medium text-accent-strong">
          {formatDateShort(date, locale)} · {time}–{hhmm((startMinute + minutes) % 1440)}
          {person ? ` · ${person.displayName}` : ''} · {fromPrice ? 'v.a. ' : ''}
          {formatEuro(price)}
        </p>
      )}

      {conflict ? (
        <div role="alert" className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-warning-soft px-3 py-3 text-sm">
          <p className="font-semibold text-warning">{conflict.message}</p>
          {conflict.alternatives.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted">{t('appt.alternatives')}</span>
              {conflict.alternatives.map((a) => (
                <button
                  key={a.start}
                  type="button"
                  className="rounded-full border border-line bg-surface px-3 py-1"
                  onClick={() => {
                    setDate(dateOf(a.start, tz));
                    setTime(hhmm(minuteOfDay(a.start, tz)));
                    setConflict(null);
                  }}
                >
                  {formatTime(a.start, tz)}
                </button>
              ))}
            </div>
          )}
          {conflict.canForce && role !== 'staff' && (
            <Button type="button" variant="secondary" className="self-start" onClick={() => save.mutate(true)} disabled={save.isPending}>
              {t('appt.force')}
            </Button>
          )}
        </div>
      ) : existing ? (
        <div role="alert" className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-warning-soft px-3 py-3 text-sm">
          <p className="font-semibold text-warning">{(save.error as ApiError).message}</p>
          <Button
            type="button"
            variant="secondary"
            className="self-start"
            onClick={() => {
              setCustomer(existing);
              setNewClient(null);
              save.reset();
            }}
          >
            {existing.firstName} {existing.lastName ?? ''}
          </Button>
        </div>
      ) : (
        <ErrorNote message={messageOf(save.error, t('error.generic'))} />
      )}

      <Button type="submit" disabled={!complete || save.isPending || (!editing && !!newClient && !newClient.firstName.trim())}>
        {editing ? t('appt.save') : t('appt.create')}
      </Button>
    </form>
  );
}

// ---------- View ----------

export function AppointmentView({
  orgId,
  id,
  tz,
  staff,
  onEdit,
  onNext,
  onToast,
}: {
  orgId: string;
  id: string;
  tz: string;
  staff: StaffOption[];
  onEdit: (a: AppointmentDetail) => void;
  onNext: (prefill: Prefill) => void;
  onToast: (message: string, undo?: () => void) => void;
}) {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const appt = useAppointment(orgId, id);
  const [weeks, setWeeks] = useState(6);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const status = useMutation({
    mutationFn: (input: { action: 'cancel'; by: 'client' | 'salon' } | { action: 'no_show' } | { action: 'restore' }) =>
      api<{ appointment: AppointmentDetail }>(`/orgs/${orgId}/appointments/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ ...input, version: appt.data!.version }),
      }),
    onSuccess: (r, input) => {
      qc.setQueryData(['appointment', orgId, id], r.appointment);
      refreshCalendar(qc, orgId);
      setConfirmCancel(false);
      if (input.action === 'no_show' || input.action === 'cancel') {
        const version = r.appointment.version;
        onToast(t(input.action === 'no_show' ? 'toast.noShow' : 'toast.cancelled'), () => {
          void api<{ appointment: AppointmentDetail }>(`/orgs/${orgId}/appointments/${id}/status`, { method: 'POST', body: JSON.stringify({ action: 'restore', version }) })
            .then((res) => {
              qc.setQueryData(['appointment', orgId, id], res.appointment);
              refreshCalendar(qc, orgId);
              onToast(t('toast.undone'));
            })
            .catch((e: unknown) => onToast(messageOf(e, t('error.generic'))!));
        });
      }
    },
  });

  if (appt.isPending) return <p className="text-muted">{t('common.loading')}</p>;
  if (!appt.data) return <ErrorNote message={messageOf(appt.error, t('error.generic'))} />;
  const a = appt.data;
  const c = a.customer;
  const date = dateOf(a.startsAt, tz);
  const active = a.status === 'booked' || a.status === 'client_confirmed' || a.status === 'requested' || a.status === 'completed';
  const started = Date.parse(a.startsAt) <= Date.now();
  const staffName = (sid: string) => staff.find((s) => s.id === sid)?.displayName ?? '';
  const whatsapp = c?.phone ? `https://wa.me/${c.phone.replace(/\D/g, '')}` : null;

  return (
    <div className="flex flex-col gap-5">
      {/* Client card */}
      <section className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-line bg-surface p-4">
        {c ? (
          <>
            <p className="text-lg font-semibold">{c.name}</p>
            <p className="text-sm text-muted">
              {c.visits === 0 ? t('appt.firstVisit') : c.visits === 1 ? t('appt.visit') : t('appt.visits', { n: c.visits })}
              {c.noShows > 0 && <span className="text-danger"> · {t('appt.noShows', { n: c.noShows })}</span>}
            </p>
            {c.alertNote && <p className="rounded-[var(--radius-control)] bg-warning-soft px-3 py-2 text-sm text-warning">⚠ {c.alertNote}</p>}
            {c.phone && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="tabular-nums">{formatPhone(c.phone)}</span>
                <a className="rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-subtle" href={`tel:${c.phone}`}>
                  {t('appt.call')}
                </a>
                {whatsapp && (
                  <a className="rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-subtle" href={whatsapp} target="_blank" rel="noreferrer">
                    {t('appt.whatsapp')}
                  </a>
                )}
              </div>
            )}
            {c.email && <p className="text-sm text-muted">{c.email}</p>}
          </>
        ) : (
          <p className="font-semibold">{a.source === 'walk_in' ? t('cal.walkIn') : t('cal.noClient')}</p>
        )}
      </section>

      {/* What and when */}
      <section className="flex flex-col gap-1">
        {a.status !== 'booked' && (
          <span className={`self-start rounded-full px-2.5 py-0.5 text-xs font-semibold ${a.status === 'completed' ? 'bg-accent-soft text-accent-strong' : 'bg-danger-soft text-danger'}`}>
            {t(`status.${a.status}` as TKey)}
          </span>
        )}
        <p className="text-[17px] font-semibold first-letter:uppercase">{formatDateLong(date, locale)}</p>
        <p className="tabular-nums">
          {formatTime(a.startsAt, tz)}–{formatTime(a.endsAt, tz)}
        </p>
        <ul className="mt-2 flex flex-col gap-1">
          {a.items.map((i) => (
            <li key={i.id} className="flex justify-between gap-3">
              <span>
                {i.name} <span className="text-muted">· {staffName(i.staffMemberId)}</span>
                {i.staffRequested && <span title={t('cal.requested')}> ♥</span>}
              </span>
              <span className="tabular-nums">{formatEuro(i.priceCents)}</span>
            </li>
          ))}
        </ul>
        {a.items.length > 1 && (
          <p className="flex justify-between border-t border-line pt-1 font-semibold">
            <span>{t('appt.total')}</span>
            <span className="tabular-nums">{formatEuro(a.totalPriceCents)}</span>
          </p>
        )}
        {a.internalNote && <p className="mt-2 whitespace-pre-line rounded-[var(--radius-control)] bg-subtle px-3 py-2 text-sm">{a.internalNote}</p>}
      </section>

      {/* Actions */}
      {a.canEdit && (
        <section className="flex flex-col gap-3">
          {active && a.status !== 'completed' && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => onEdit(a)}>
                {t('appt.edit')}
              </Button>
              {started && (
                <Button variant="secondary" disabled={status.isPending} onClick={() => status.mutate({ action: 'no_show' })}>
                  {t('appt.noShow')}
                </Button>
              )}
              <Button variant="ghost" className="text-danger" onClick={() => setConfirmCancel(true)}>
                {t('appt.cancel')}
              </Button>
            </div>
          )}
          {a.status === 'completed' && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" disabled={status.isPending} onClick={() => status.mutate({ action: 'no_show' })}>
                {t('appt.noShow')}
              </Button>
            </div>
          )}
          {(a.status === 'cancelled' || a.status === 'no_show') && (
            <Button variant="secondary" className="self-start" disabled={status.isPending} onClick={() => status.mutate({ action: 'restore' })}>
              {t('appt.restore')}
            </Button>
          )}
          {confirmCancel && (
            <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line bg-surface p-3">
              <p className="font-semibold">{t('appt.cancelWho')}</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => status.mutate({ action: 'cancel', by: 'client' })} disabled={status.isPending}>
                  {t('appt.cancelByClient')}
                </Button>
                <Button variant="secondary" onClick={() => status.mutate({ action: 'cancel', by: 'salon' })} disabled={status.isPending}>
                  {t('appt.cancelBySalon')}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmCancel(false)}>
                  {t('appt.keep')}
                </Button>
              </div>
            </div>
          )}
          <ErrorNote message={messageOf(status.error, t('error.generic'))} />

          {/* Volgende afspraak: same services and person, X weeks later */}
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
            <Button
              variant="secondary"
              onClick={() =>
                onNext({
                  date: addDays(date, weeks * 7),
                  minute: minuteOfDay(a.startsAt, tz),
                  staffId: a.items[0]?.staffMemberId ?? null,
                  items: a.items.map((i) => ({ serviceId: i.serviceId, variantId: i.variantId })),
                  customer: c ? { id: c.id, firstName: c.firstName, lastName: c.lastName, phone: c.phone, email: c.email, alertNote: c.alertNote } : null,
                })
              }
            >
              {t('appt.nextAppointment')}
            </Button>
            <select
              aria-label={t('appt.nextAppointment')}
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-2 text-base"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {t('appt.inWeeks', { n })}
                </option>
              ))}
            </select>
          </div>
        </section>
      )}

      {/* History */}
      <section className="flex flex-col gap-1 border-t border-line pt-3">
        <h3 className="text-sm font-semibold">{t('appt.history')}</h3>
        <ul className="flex flex-col gap-0.5 text-sm text-muted">
          {a.events.map((e, i) => (
            <li key={i}>
              {e.type === 'created' && e.data?.source === 'online' ? t('event.online') : t(`event.${e.type}` as TKey)} · {formatStamp(e.at, tz, locale)}
              {e.actorName && ` · ${t('event.by', { name: e.actorName })}`}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted">#{a.reference}</p>
      </section>
    </div>
  );
}
